// ListedOwnerMiniCard.jsx
import {
  Card,
  CardActionArea,
  CardContent,
  Avatar,
  Stack,
  Typography,
  Chip,
  Tooltip,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import VerifiedIcon from "@mui/icons-material/Verified";
import LaunchIcon from "@mui/icons-material/Launch";

export default function ListedOwnerMiniCard({ owner }) {
  if (!owner) return null;

  const licenses = Array.isArray(owner.licenses) ? owner.licenses : [];
  const isBroker = licenses.some(
    (l) =>
      String(l.license_type || "").toLowerCase() === "broker" && l.is_verified
  );
  const isRealtor = licenses.some(
    (l) =>
      String(l.license_type || "").toLowerCase() === "realtor" && l.is_verified
  );

  const fullName =
    [owner.first_name, owner.last_name].filter(Boolean).join(" ") ||
    "View profile";

  const to = owner.slug
    ? `/Realtors-and-Brokers/${owner.slug}`
    : `/Realtors-and-Brokers/${owner.pro_id}`;

  const hasAnyVerified = isBroker || isRealtor;
  const roleLabel =
    isBroker && isRealtor
      ? "Broker & Realtor"
      : isBroker
      ? "Broker"
      : isRealtor
      ? "Realtor"
      : null;

  return (
    <Card
      variant="outlined"
      sx={{
        mt: 2,
        position: { xs: "static", md: "sticky" },
        top: 16,
        borderRadius: 2,
        boxShadow: 2,
        borderColor: "divider",
        overflow: "hidden",
        transition: "box-shadow .2s ease, transform .2s ease",
        "&:hover": { boxShadow: 6, transform: "translateY(-1px)" },
      }}
    >
      <CardActionArea
        component={RouterLink}
        to={to}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`Open profile for ${fullName}`}
      >
        <CardContent
          sx={{ display: "flex", alignItems: "center", gap: 2, p: 2 }}
        >
          {/* Larger photo with subtle ring */}
          <Avatar
            src={owner.image || undefined}
            alt={fullName}
            sx={(theme) => ({
              width: 72,
              height: 72,
              boxShadow: `0 0 0 3px ${theme.palette.background.paper}, 0 0 0 5px ${theme.palette.divider}`,
              fontWeight: 700,
            })}
          >
            {(owner.first_name?.[0] || "").toUpperCase()}
            {(owner.last_name?.[0] || "").toUpperCase()}
          </Avatar>

          {/* Text block */}
          <Stack sx={{ flex: 1, textAlign: "left", minWidth: 0 }} spacing={0.5}>
            <Typography variant="caption" color="text.secondary">
              Listed by
            </Typography>

            {/* Name + open-in-new hint */}
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              sx={{ minWidth: 0 }}
            >
              <Tooltip title={fullName} placement="top">
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  noWrap
                  sx={{ lineHeight: 1.2, flex: "0 1 auto", minWidth: 0 }}
                >
                  {fullName}
                </Typography>
              </Tooltip>
              <LaunchIcon fontSize="small" sx={{ opacity: 0.5 }} />
              {hasAnyVerified && (
                <VerifiedIcon
                  fontSize="small"
                  color="primary"
                  sx={{ ml: 0.25 }}
                  titleAccess="Verified license"
                />
              )}
            </Stack>

            {/* Role badge (left-aligned under name) */}
            {roleLabel ? (
              <Chip
                label={roleLabel}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ alignSelf: "flex-start", mt: 0.25 }}
              />
            ) : (
              <Chip
                label="Not a verified agent"
                size="small"
                color="error"
                variant="outlined"
                sx={{ alignSelf: "flex-start", mt: 0.25 }}
              />
            )}
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}
